import { KeyboardEvent, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Box, Tooltip, Typography } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';
import { pluralizeString } from '../../utils/common';
import { Flex, FlexCenter } from '../Flex';
import useAccount from '../../hooks/useAccount';
import useAiChatPanel from '../../hooks/useAiChatPanel';

const Wrapper = styled(Flex)<{ $bottom?: number; $sticky?: boolean }>`
  justify-content: center;
  bottom: ${({ $bottom }) => $bottom || 48}px;
  position: ${({ $sticky }) => ($sticky ? 'sticky' : 'absolute')};
  width: 100%;
  z-index: 1;
`;

const FloatingBox = styled(Flex)`
  border-radius: 24px;
  background: rgba(221, 221, 221, 0.34);
  filter: drop-shadow(0px 8px 24px rgba(0, 0, 0, 0.1));
  backdrop-filter: blur(12px);
  align-items: center;
  padding: 0 24px;
  height: 45px;
`;

const CollapsibleSection = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-columns: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-columns 0.2s ease;
  align-self: stretch;
`;

const CollapsibleInner = styled(Flex)`
  overflow: hidden;
  min-width: 0;
  align-items: center;
  align-self: stretch;
`;

const CountAndActionsGroup = styled(Flex)`
  align-items: center;
  gap: 16px;
`;

const ActionsGroup = styled(Flex)`
  align-items: center;
`;

const CountSection = styled(FlexCenter)`
  white-space: nowrap;
`;

const ActionIconButton = styled(FlexCenter)<{ $disabled?: boolean }>`
  padding: 8px;
  cursor: pointer;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: background-color 0.2s;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.palette.common.black};
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  width: 150px;

  &::placeholder {
    color: ${({ theme }) => theme.palette.common.purple};
  }
`;

const SendButton = styled(FlexCenter)`
  cursor: pointer;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.common.gray4};

  &:hover {
    color: ${({ theme }) => theme.palette.common.black};
  }
`;

const VerticalSeparator = styled(Box)`
  width: 0;
  align-self: center;
  border-right: 1px solid ${({ theme }) => theme.palette.common.gray18};
  height: 21px;
`;

type MenuButtonProps = {
  label?: string;
  icon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
};

export const ActionsMenuButton = ({
  label,
  icon,
  onClick,
  disabled,
  tooltip,
}: MenuButtonProps) => (
  <Tooltip title={tooltip || label || ''} placement="top">
    <div aria-label={label}>
      <ActionIconButton onClick={onClick} $disabled={disabled}>
        {icon}
      </ActionIconButton>
    </div>
  </Tooltip>
);

type ActionItem = {
  icon?: ReactNode;
  endIcon?: ReactNode;
  label?: string;
  value: string;
  itemRenderer?: () => ReactNode;
  disabled?: boolean;
  tooltip?: string;
};

type Props = {
  itemCount?: number;
  countLabel?: string;
  countLabelPlural?: string;
  actions?: ActionItem[];
  onChange?: (value: string) => void;
  onClose?: () => void;
  bottom?: number;
  showWithoutSelection?: boolean;
  showAiChat?: boolean;
  sticky?: boolean;
};

const FloatingActionsMenu = ({
  itemCount = 0,
  countLabel = '',
  countLabelPlural = '',
  actions = [],
  onChange,
  onClose,
  bottom,
  showWithoutSelection = true,
  showAiChat = true,
  sticky = false,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const { data: account } = useAccount();
  const { openPanel, isExpanded: isChatPanelOpen } = useAiChatPanel();

  const handleSubmit = () => {
    if (!inputValue.trim()) return;
    openPanel({
      startMessage: inputValue.trim(),
    });
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const open = itemCount > 0;
  const showAiChatSection =
    showAiChat && !isChatPanelOpen && !!account?.ai_features_enabled;
  const hiddenByChatPanel = isChatPanelOpen && !open;
  const visible =
    !hiddenByChatPanel && (open || (showWithoutSelection && showAiChatSection));

  if (!visible) return null;

  return (
    <Wrapper $bottom={bottom} $sticky={sticky}>
      <FloatingBox>
        <CollapsibleSection $open={open}>
          <CollapsibleInner>
            <CountAndActionsGroup>
              <CountSection>
                <Typography noWrap>
                  {`${pluralizeString(
                    itemCount,
                    countLabel,
                    countLabelPlural,
                  )} selected${showAiChat ? ':' : ''}`}
                </Typography>
              </CountSection>
              <ActionsGroup>
                {actions.map((action) => (
                  <div key={action.value}>
                    {action.itemRenderer ? (
                      action.itemRenderer()
                    ) : (
                      <ActionsMenuButton
                        label={action.label}
                        icon={action.icon}
                        onClick={() => onChange?.(action.value)}
                        disabled={action.disabled}
                        tooltip={action.tooltip}
                      />
                    )}
                  </div>
                ))}
              </ActionsGroup>
            </CountAndActionsGroup>
            {showAiChatSection && <VerticalSeparator sx={{ ml: 1, mr: 2 }} />}
          </CollapsibleInner>
        </CollapsibleSection>

        <CollapsibleSection $open={showAiChatSection}>
          <CollapsibleInner>
            <StyledInput
              placeholder="Ask Elementary AI..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <SendButton onClick={handleSubmit}>
              <ArrowUpwardRoundedIcon
                sx={{ fontSize: '20px', transform: 'rotate(90deg)' }}
              />
            </SendButton>
          </CollapsibleInner>
        </CollapsibleSection>

        <CollapsibleSection $open={open}>
          <CollapsibleInner>
            <VerticalSeparator sx={{ mx: 2 }} />
            <FlexCenter>
              <ActionIconButton onClick={onClose}>
                <CloseIcon />
              </ActionIconButton>
            </FlexCenter>
          </CollapsibleInner>
        </CollapsibleSection>
      </FloatingBox>
    </Wrapper>
  );
};

export default FloatingActionsMenu;
